import React, { useState, useEffect, useContext, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import DataContext from '../../Contexts/DataContext';
import DeleteConfirmationDialog from '../../Components/DeleteConfirmation/DeleteConfirmationDialog';

/**
 * TaxonomyPanel - Manage entities, departments, and parent accounts
 * 
 * @param {number} projectId - Current project ID
 * @param {function} onTaxonomyChange - Callback when taxonomy items change
 * @param {function} onPendingChangesUpdate - Callback to update pending changes state
 */
const TaxonomyPanel = forwardRef(({ projectId, onTaxonomyChange, onPendingChangesUpdate }, ref) => {
  const context = useContext(DataContext);
  
  // State for dropdowns
  const [entities, setEntities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [parentAccounts, setParentAccounts] = useState([]);
  
  // Selected items — parent account tracks the full object { id, parent_account_name }
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedParentAccount, setSelectedParentAccount] = useState(null); // { id, parent_account_name }
  
  // Dialog states
  const [showAddEntityDialog, setShowAddEntityDialog] = useState(false);
  const [showEditEntityDialog, setShowEditEntityDialog] = useState(false);
  const [showAddDepartmentDialog, setShowAddDepartmentDialog] = useState(false);
  const [showEditDepartmentDialog, setShowEditDepartmentDialog] = useState(false);
  const [showAddParentDialog, setShowAddParentDialog] = useState(false);
  const [showEditParentDialog, setShowEditParentDialog] = useState(false);
  
  // Form data for add dialogs
  const [entityForm, setEntityForm] = useState({ entity_id: '', entity_name: '' });
  const [departmentForm, setDepartmentForm] = useState({ 
    entity_id: '', 
    department_id: '', 
    department_name: '' 
  });
  const [parentAccountForm, setParentAccountForm] = useState({ parent_account: '' });
  
  // Track pending taxonomy changes
  const [pendingChanges, setPendingChanges] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', confirmLabel: '', isDanger: true, onConfirm: null });

  // Sync entity/department lists when context updates, preserving current selections
  useEffect(() => {
    if (context.activeProjectAttributes?.entities) {
      setEntities(context.activeProjectAttributes.entities);
    }
  }, [context.activeProjectAttributes?.entities]);

  useEffect(() => {
    if (context.activeProjectAttributes?.departments) {
      setDepartments(context.activeProjectAttributes.departments);
    }
  }, [context.activeProjectAttributes?.departments]);

  // Full load on mount or project change (also reloads parent accounts)
  useEffect(() => {
    loadTaxonomyData();
  }, [projectId]);

  // Notify parent of pending changes
  useEffect(() => {
    if (onPendingChangesUpdate) {
      onPendingChangesUpdate(pendingChanges.length > 0);
    }
  }, [pendingChanges, onPendingChangesUpdate]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    saveTaxonomyChanges: handleSaveTaxonomyChanges,
    discardTaxonomyChanges: handleDiscardTaxonomyChanges,
  }));

  const loadTaxonomyData = async () => {
    if (!projectId) return;

    // Load entities from context
    if (context.activeProjectAttributes?.entities) {
      setEntities(context.activeProjectAttributes.entities);
    }

    // Load departments from context
    if (context.activeProjectAttributes?.departments) {
      setDepartments(context.activeProjectAttributes.departments);
    }

    // Load parent accounts from the taxonomy API (returns objects with id + parent_account_name)
    try {
      const parentAccountsList = await context.getParentAccounts();
      setParentAccounts(parentAccountsList || []);
    } catch (err) {
      console.error('Error loading parent accounts:', err);
    }
  };

  /**
   * Add a pending change to the queue
   */
  const addPendingChange = (change) => {
    setPendingChanges(prev => [...prev, change]);
  };

  const openConfirmDialog = (title, message, confirmLabel, onConfirm, isDanger = true) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, isDanger, onConfirm });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  /**
   * Save all pending taxonomy changes
   */
  const handleSaveTaxonomyChanges = async () => {
    if (pendingChanges.length === 0) {
      toast.info('No taxonomy changes to save');
      return;
    }

    setIsSaving(true);
    const results = {
      successful: 0,
      failed: [],
    };

    try {
      const failedPendingChanges = [];
      for (const change of pendingChanges) {
        let succeeded = false;
        try {
          switch (change.type) {
            case 'create_entity': {
              const createdEntity = await context.createEntity(change.data);
              if (createdEntity) {
                results.successful++;
                succeeded = true;
              } else {
                results.failed.push({ item: change.description, reason: 'Failed to create entity' });
              }
              break;
            }
            case 'update_entity': {
              const updatedEntity = await context.updateEntity(change.entityId, change.data);
              if (updatedEntity) {
                results.successful++;
                succeeded = true;
              } else {
                results.failed.push({ item: change.description, reason: 'Failed to update entity' });
              }
              break;
            }
            case 'delete_entity': {
              const deletedEntity = await context.deleteEntity(change.entityId);
              if (deletedEntity) {
                results.successful++;
                succeeded = true;
              } else {
                results.failed.push({ item: change.description, reason: 'Failed to delete entity' });
              }
              break;
            }
            case 'create_department': {
              const createdDept = await context.createDepartment(change.data);
              if (createdDept) {
                results.successful++;
                succeeded = true;
              } else {
                results.failed.push({ item: change.description, reason: 'Failed to create department' });
              }
              break;
            }
            case 'update_department': {
              const updatedDept = await context.updateDepartment(change.entityId, change.departmentId, change.data);
              if (updatedDept) {
                results.successful++;
                succeeded = true;
              } else {
                results.failed.push({ item: change.description, reason: 'Failed to update department' });
              }
              break;
            }
            case 'delete_department': {
              const deletedDept = await context.deleteDepartment(change.entityId, change.departmentId);
              if (deletedDept) {
                results.successful++;
                succeeded = true;
              } else {
                results.failed.push({ item: change.description, reason: 'Failed to delete department' });
              }
              break;
            }
            case 'create_parent_account': {
              const created = await context.createParentAccount(change.data);
              if (created) {
                results.successful++;
                succeeded = true;
              } else {
                results.failed.push({ item: change.description, reason: 'Failed to create parent account' });
              }
              break;
            }
            case 'update_parent_account': {
              const updated = await context.updateParentAccount(change.parentAccountId, change.data);
              if (updated) {
                results.successful++;
                succeeded = true;
              } else {
                results.failed.push({ item: change.description, reason: 'Failed to update parent account' });
              }
              break;
            }
            case 'delete_parent_account': {
              const deleted = await context.deleteParentAccount(change.parentAccountId);
              if (deleted) {
                results.successful++;
                succeeded = true;
              } else {
                results.failed.push({ item: change.description, reason: 'Failed to delete parent account' });
              }
              break;
            }
            default:
              results.failed.push({
                item: change.description,
                reason: 'Unknown change type',
              });
          }
        } catch (error) {
          console.error('Error applying change:', error);
          results.failed.push({
            item: change.description,
            reason: error.message || 'Failed to apply change',
          });
          succeeded = false;
        }
        // Retain failed changes so the user can retry without re-entering data.
        if (!succeeded) failedPendingChanges.push(change);
      }

      // Only remove the changes that succeeded; failed ones remain in the queue.
      setPendingChanges(failedPendingChanges);

      // Reload taxonomy data
      await loadTaxonomyData();
      if (onTaxonomyChange) onTaxonomyChange();

      // Show results
      if (results.successful > 0) {
        toast.success(`Successfully saved ${results.successful} taxonomy change(s)`);
      }
      if (results.failed.length > 0) {
        toast.error(`Failed to save ${results.failed.length} change(s)`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Discard all pending taxonomy changes
   */
  const handleDiscardTaxonomyChanges = (skipConfirm = false) => {
    if (!skipConfirm) {
      openConfirmDialog(
        'Discard Taxonomy Changes',
        'Are you sure you want to discard all unsaved taxonomy changes?',
        'Discard',
        () => {
          closeConfirmDialog();
          setPendingChanges([]);
          toast.info('Taxonomy changes discarded');
        },
        false
      );
      return;
    }
    setPendingChanges([]);
  };

  // Entity handlers
  const handleAddEntity = () => {
    setEntityForm({ entity_id: '', entity_name: '' });
    setShowAddEntityDialog(true);
  };

  const handleEditEntity = () => {
    if (!selectedEntity) {
      toast.warning('Please select an entity to edit');
      return;
    }
    
    const entity = entities.find(e => String(e.entity_id) === String(selectedEntity));
    if (entity) {
      setEntityForm({ entity_id: String(entity.entity_id), entity_name: entity.entity_name });
      setShowEditEntityDialog(true);
    }
  };

  const handleSaveEntity = async () => {
    if (!entityForm.entity_id || !entityForm.entity_name) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!/^\d+$/.test(entityForm.entity_id)) {
      toast.error('Entity ID must be numeric');
      return;
    }

    addPendingChange({
      type: 'create_entity',
      data: { entity_id: entityForm.entity_id, entity_name: entityForm.entity_name },
      description: `Create entity ${entityForm.entity_id} - ${entityForm.entity_name}`,
    });

    toast.info('Entity queued for save. Click "Save Taxonomy Changes" to apply.');
    setShowAddEntityDialog(false);
  };

  const handleUpdateEntity = async () => {
    if (!entityForm.entity_name) {
      toast.error('Please enter an entity name');
      return;
    }

    addPendingChange({
      type: 'update_entity',
      entityId: entityForm.entity_id,
      data: { entity_name: entityForm.entity_name },
      description: `Update entity ${entityForm.entity_id} to ${entityForm.entity_name}`,
    });

    toast.info('Entity update queued for save. Click "Save Taxonomy Changes" to apply.');
    setShowEditEntityDialog(false);
  };

  const handleDeleteEntity = async () => {
    if (!selectedEntity) {
      toast.warning('Please select an entity to delete');
      return;
    }

    const entity = entities.find(e => String(e.entity_id) === String(selectedEntity));
    openConfirmDialog(
      'Delete Entity',
      `Delete entity ${selectedEntity}${entity ? ` – ${entity.entity_name}` : ''}? This will be queued for save.`,
      'Delete',
      () => {
        closeConfirmDialog();
        addPendingChange({
          type: 'delete_entity',
          entityId: selectedEntity,
          description: `Delete entity ${selectedEntity}${entity ? ` - ${entity.entity_name}` : ''}`,
        });
        setSelectedEntity('');
        setSelectedDepartment('');
        toast.info('Entity deletion queued for save. Click "Save Taxonomy Changes" to apply.');
      }
    );
  };

  // Department handlers
  const handleAddDepartment = () => {
    setDepartmentForm({ entity_id: '', department_id: '', department_name: '' });
    setShowAddDepartmentDialog(true);
  };

  const handleEditDepartment = () => {
    if (!selectedDepartment) {
      toast.warning('Please select a department to edit');
      return;
    }
    
    const [entityId, departmentId] = selectedDepartment.split('-');
    const dept = departments.find(d => 
      String(d.entityId) === entityId && String(d.departmentId) === departmentId
    );
    
    if (dept) {
      setDepartmentForm({ 
        entity_id: dept.entityId, 
        department_id: dept.departmentId, 
        department_name: dept.departmentName 
      });
      setShowEditDepartmentDialog(true);
    }
  };

  const handleSaveDepartment = async () => {
    if (!departmentForm.entity_id || !departmentForm.department_id || !departmentForm.department_name) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate IDs are numeric
    if (!/^\d+$/.test(departmentForm.entity_id) || !/^\d+$/.test(departmentForm.department_id)) {
      toast.error('Entity ID and Department ID must be numeric');
      return;
    }

    // Queue the change instead of saving immediately
    addPendingChange({
      type: 'create_department',
      data: departmentForm,
      description: `Create department ${departmentForm.entity_id}-${departmentForm.department_id} - ${departmentForm.department_name}`,
    });

    toast.info('Department queued for save. Click "Save Taxonomy Changes" to apply.');
    setShowAddDepartmentDialog(false);
  };

  const handleUpdateDepartment = async () => {
    if (!departmentForm.department_name) {
      toast.error('Please enter a department name');
      return;
    }

    // Queue the change instead of saving immediately
    addPendingChange({
      type: 'update_department',
      entityId: departmentForm.entity_id,
      departmentId: departmentForm.department_id,
      data: { department_name: departmentForm.department_name },
      description: `Update department ${departmentForm.entity_id}-${departmentForm.department_id} to ${departmentForm.department_name}`,
    });

    toast.info('Department update queued for save. Click "Save Taxonomy Changes" to apply.');
    setShowEditDepartmentDialog(false);
  };

  const handleDeleteDepartment = async () => {
    if (!selectedDepartment) {
      toast.warning('Please select a department to delete');
      return;
    }

    const [entityId, departmentId] = selectedDepartment.split('-');
    const dept = departments.find(d =>
      String(d.entityId) === entityId && String(d.departmentId) === departmentId
    );

    openConfirmDialog(
      'Delete Department',
      `Delete department ${entityId}-${departmentId}${dept ? ` – ${dept.departmentName}` : ''}? This will be queued for save.`,
      'Delete',
      () => {
        closeConfirmDialog();
        addPendingChange({
          type: 'delete_department',
          entityId,
          departmentId,
          description: `Delete department ${entityId}-${departmentId}${dept ? ` - ${dept.departmentName}` : ''}`,
        });
        setSelectedDepartment('');
        toast.info('Department deletion queued for save. Click "Save Taxonomy Changes" to apply.');
      }
    );
  };

  // Parent Account handlers
  const handleAddParentAccount = () => {
    setParentAccountForm({ parent_account: '' });
    setShowAddParentDialog(true);
  };

  const handleEditParentAccount = () => {
    if (!selectedParentAccount) {
      toast.warning('Please select a parent account to edit');
      return;
    }
    
    setParentAccountForm({ parent_account: selectedParentAccount.parent_account_name });
    setShowEditParentDialog(true);
  };

  const handleSaveParentAccount = async () => {
    if (!parentAccountForm.parent_account) {
      toast.error('Please enter a parent account name');
      return;
    }

    addPendingChange({
      type: 'create_parent_account',
      data: { parent_account_name: parentAccountForm.parent_account },
      description: `Create parent account "${parentAccountForm.parent_account}"`,
    });

    toast.info('Parent account queued for save. Click "Save Taxonomy Changes" to apply.');
    setShowAddParentDialog(false);
  };

  const handleUpdateParentAccount = async () => {
    if (!parentAccountForm.parent_account) {
      toast.error('Please enter a parent account name');
      return;
    }

    if (!selectedParentAccount) {
      toast.error('No parent account selected');
      return;
    }

    addPendingChange({
      type: 'update_parent_account',
      parentAccountId: selectedParentAccount.id,
      data: { parent_account_name: parentAccountForm.parent_account },
      description: `Update parent account "${selectedParentAccount.parent_account_name}" to "${parentAccountForm.parent_account}"`,
    });

    toast.info('Parent account update queued for save. Click "Save Taxonomy Changes" to apply.');
    setShowEditParentDialog(false);
  };

  const handleDeleteParentAccount = async () => {
    if (!selectedParentAccount) {
      toast.warning('Please select a parent account to delete');
      return;
    }

    openConfirmDialog(
      'Delete Parent Account',
      `Delete parent account "${selectedParentAccount.parent_account_name}"? This will be queued for save.`,
      'Delete',
      () => {
        closeConfirmDialog();
        addPendingChange({
          type: 'delete_parent_account',
          parentAccountId: selectedParentAccount.id,
          description: `Delete parent account "${selectedParentAccount.parent_account_name}"`,
        });
        setSelectedParentAccount(null);
        toast.info('Parent account deletion queued for save. Click "Save Taxonomy Changes" to apply.');
      }
    );
  };

  return (
    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Taxonomy Management
        </Typography>
        
        {/* Save/Discard buttons */}
        {pendingChanges.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDiscardTaxonomyChanges}
              disabled={isSaving}
            >
              Discard Changes ({pendingChanges.length})
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSaveTaxonomyChanges}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : `Save Taxonomy Changes (${pendingChanges.length})`}
            </Button>
          </Box>
        )}
      </Box>

      {/* Entities Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography sx={{ minWidth: 120 }}>Entities:</Typography>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Select Entity</InputLabel>
          <Select
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
            label="Select Entity"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {entities.map((entity) => (
              <MenuItem key={entity.entity_id} value={entity.entity_id}>
                {entity.entity_id} - {entity.entity_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddEntity}
        >
          Add
        </Button>
        <IconButton
          size="small"
          color="primary"
          onClick={handleEditEntity}
          disabled={!selectedEntity}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={handleDeleteEntity}
          disabled={!selectedEntity}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      {/* Departments Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography sx={{ minWidth: 120 }}>Departments:</Typography>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Select Department</InputLabel>
          <Select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            label="Select Department"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {departments.map((dept) => (
              <MenuItem 
                key={`${dept.entityId}-${dept.departmentId}`} 
                value={`${dept.entityId}-${dept.departmentId}`}
              >
                {dept.entityId}-{dept.departmentId} - {dept.departmentName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddDepartment}
        >
          Add
        </Button>
        <IconButton
          size="small"
          color="primary"
          onClick={handleEditDepartment}
          disabled={!selectedDepartment}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={handleDeleteDepartment}
          disabled={!selectedDepartment}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      {/* Parent Accounts Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ minWidth: 120 }}>Parent Accounts:</Typography>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Select Parent Account</InputLabel>
          <Select
            value={selectedParentAccount ? selectedParentAccount.id : ''}
            onChange={(e) => {
              const val = e.target.value;
              // API returns numeric ids; MUI Select passes strings — coerce for comparison
              const pa = parentAccounts.find(p => String(p.id) === String(val));
              setSelectedParentAccount(pa || null);
            }}
            label="Select Parent Account"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {parentAccounts.map((pa) => (
              <MenuItem key={pa.id} value={pa.id}>
                {pa.parent_account_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddParentAccount}
        >
          Add
        </Button>
        <IconButton
          size="small"
          color="primary"
          onClick={handleEditParentAccount}
          disabled={!selectedParentAccount}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={handleDeleteParentAccount}
          disabled={!selectedParentAccount}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      {/* Add Entity Dialog */}
      <Dialog open={showAddEntityDialog} onClose={() => setShowAddEntityDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Entity</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Entity ID"
            type="text"
            fullWidth
            value={entityForm.entity_id}
            onChange={(e) => setEntityForm({ ...entityForm, entity_id: e.target.value })}
            helperText="Must be numeric"
          />
          <TextField
            margin="dense"
            label="Entity Name"
            type="text"
            fullWidth
            value={entityForm.entity_name}
            onChange={(e) => setEntityForm({ ...entityForm, entity_name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddEntityDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEntity} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Entity Dialog */}
      <Dialog open={showEditEntityDialog} onClose={() => setShowEditEntityDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Entity</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Entity ID"
            type="text"
            fullWidth
            value={entityForm.entity_id}
            disabled
            helperText="Entity ID cannot be changed"
          />
          <TextField
            autoFocus
            margin="dense"
            label="Entity Name"
            type="text"
            fullWidth
            value={entityForm.entity_name}
            onChange={(e) => setEntityForm({ ...entityForm, entity_name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditEntityDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateEntity} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add Department Dialog */}
      <Dialog open={showAddDepartmentDialog} onClose={() => setShowAddDepartmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Department</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Entity</InputLabel>
            <Select
              value={departmentForm.entity_id}
              onChange={(e) => setDepartmentForm({ ...departmentForm, entity_id: e.target.value })}
              label="Entity"
            >
              {entities.map((entity) => (
                <MenuItem key={entity.entity_id} value={entity.entity_id}>
                  {entity.entity_id} - {entity.entity_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Department ID"
            type="text"
            fullWidth
            value={departmentForm.department_id}
            onChange={(e) => setDepartmentForm({ ...departmentForm, department_id: e.target.value })}
            helperText="Must be numeric"
          />
          <TextField
            margin="dense"
            label="Department Name"
            type="text"
            fullWidth
            value={departmentForm.department_name}
            onChange={(e) => setDepartmentForm({ ...departmentForm, department_name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDepartmentDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDepartment} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Department Dialog */}
      <Dialog open={showEditDepartmentDialog} onClose={() => setShowEditDepartmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Department</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Entity</InputLabel>
            <Select
              value={departmentForm.entity_id}
              disabled
              label="Entity"
            >
              {entities.map((entity) => (
                <MenuItem key={entity.entity_id} value={entity.entity_id}>
                  {entity.entity_id} - {entity.entity_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Department ID"
            type="text"
            fullWidth
            value={departmentForm.department_id}
            disabled
            helperText="Department ID cannot be changed"
          />
          <TextField
            autoFocus
            margin="dense"
            label="Department Name"
            type="text"
            fullWidth
            value={departmentForm.department_name}
            onChange={(e) => setDepartmentForm({ ...departmentForm, department_name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDepartmentDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateDepartment} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add Parent Account Dialog */}
      <Dialog open={showAddParentDialog} onClose={() => setShowAddParentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Parent Account</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Parent Account Name"
            type="text"
            fullWidth
            value={parentAccountForm.parent_account}
            onChange={(e) => setParentAccountForm({ ...parentAccountForm, parent_account: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddParentDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveParentAccount} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Parent Account Dialog */}
      <Dialog open={showEditParentDialog} onClose={() => setShowEditParentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Parent Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This will update the parent account name for all accounts that currently use "{selectedParentAccount?.parent_account_name}".
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Parent Account Name"
            type="text"
            fullWidth
            value={parentAccountForm.parent_account}
            onChange={(e) => setParentAccountForm({ ...parentAccountForm, parent_account: e.target.value })}
            helperText="All accounts using this parent account will be updated"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditParentDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateParentAccount} variant="contained">Update All</Button>
        </DialogActions>
      </Dialog>

      {/* Delete / Discard Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        isDanger={confirmDialog.isDanger}
        onConfirm={confirmDialog.onConfirm || closeConfirmDialog}
        onCancel={closeConfirmDialog}
      />
    </Box>
  );
});

export default TaxonomyPanel;
